/**
 * TanStack Query hooks for Budget Management (011)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  budgetService,
  getOverview,
  listBudgets,
  listBudgetsForPeriod,
  getActiveAlerts,
  getDashboardSummary,
  createBudget,
  updateBudget,
  deleteBudget,
} from '../services/budget-service';
import {
  savingsGoalService,
  getSummary as getSavingsSummary,
  listGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  addProgress,
} from '../services/savings-goal-service';
import {
  billService,
  getBillSummary,
  listBills,
  getQuickReminders,
  createBill,
  updateBill,
  deleteBill,
  markPaid,
} from '../services/bill-service';
import {
  notificationService,
  listUnread,
  getUnreadCount,
  markRead,
  dismiss,
  syncBudgetAlerts,
} from '../services/notification-service';
import type { BudgetPeriodType, CreateBudgetInput, UpdateBudgetInput } from '../types/budget.types';
import type {
  CreateSavingsGoalInput,
  UpdateSavingsGoalInput,
  AddProgressInput,
} from '../types/savings-goal.types';
import type { CreateBillInput, UpdateBillInput } from '../types/bill.types';

export const budgetKeys = {
  all: ['budgets'] as const,
  overview: (period: BudgetPeriodType, anchor: string) =>
    [...budgetKeys.all, 'overview', period, anchor] as const,
  plans: (period: BudgetPeriodType, anchor: string) =>
    [...budgetKeys.all, 'plans', period, anchor] as const,
  list: () => [...budgetKeys.all, 'list'] as const,
  alerts: () => [...budgetKeys.all, 'alerts'] as const,
  dashboardSummary: () => [...budgetKeys.all, 'dashboard-summary'] as const,
};

export const savingsGoalKeys = {
  all: ['savings-goals'] as const,
  list: () => [...savingsGoalKeys.all, 'list'] as const,
  summary: () => [...savingsGoalKeys.all, 'summary'] as const,
};

export const billKeys = {
  all: ['bills'] as const,
  list: (filter?: string) => [...billKeys.all, 'list', filter ?? 'all'] as const,
  summary: () => [...billKeys.all, 'summary'] as const,
  reminders: () => [...billKeys.all, 'reminders'] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
  count: () => [...notificationKeys.all, 'count'] as const,
};

async function invalidateBudgetQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: budgetKeys.all }),
    queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  ]);
}

export function useBudgetOverview(periodType: BudgetPeriodType, periodAnchor: string) {
  return useQuery({
    queryKey: budgetKeys.overview(periodType, periodAnchor),
    queryFn: async () => {
      const result = await getOverview(periodType, periodAnchor);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    staleTime: 2 * 60 * 1000,
  });
}

/** Active plans for a period grain, scored against the selected calendar window. */
export function useBudgetPlans(periodType: BudgetPeriodType, periodAnchor: string) {
  return useQuery({
    queryKey: budgetKeys.plans(periodType, periodAnchor),
    queryFn: async () => {
      const result = await listBudgetsForPeriod(periodType, periodAnchor);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useBudgets() {
  return useQuery({
    queryKey: budgetKeys.list(),
    queryFn: async () => {
      const result = await listBudgets();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useBudgetAlerts() {
  return useQuery({
    queryKey: budgetKeys.alerts(),
    queryFn: async () => {
      const result = await getActiveAlerts();
      if (result.error) throw new Error(result.error.message);
      const alerts = result.data ?? [];
      await syncBudgetAlerts(alerts);
      return alerts;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useDashboardBudgetSummary() {
  return useQuery({
    queryKey: budgetKeys.dashboardSummary(),
    queryFn: async () => {
      const result = await getDashboardSummary();
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBudgetInput) => {
      const result = await createBudget(input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => invalidateBudgetQueries(queryClient),
  });
}

export function useUpdateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateBudgetInput }) => {
      const result = await updateBudget(id, input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => invalidateBudgetQueries(queryClient),
  });
}

export function useDeleteBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteBudget(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => invalidateBudgetQueries(queryClient),
  });
}

export function useSavingsGoals() {
  return useQuery({
    queryKey: savingsGoalKeys.list(),
    queryFn: async () => {
      const result = await listGoals();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useSavingsGoalSummary() {
  return useQuery({
    queryKey: savingsGoalKeys.summary(),
    queryFn: async () => {
      const result = await getSavingsSummary();
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateSavingsGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSavingsGoalInput) => {
      const result = await createGoal(input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savingsGoalKeys.all }),
  });
}

export function useUpdateSavingsGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateSavingsGoalInput }) => {
      const result = await updateGoal(id, input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savingsGoalKeys.all }),
  });
}

export function useDeleteSavingsGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteGoal(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savingsGoalKeys.all }),
  });
}

export function useAddGoalProgress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddProgressInput) => {
      const result = await addProgress(input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savingsGoalKeys.all }),
  });
}

export function useBills(filter?: string) {
  return useQuery({
    queryKey: billKeys.list(filter),
    queryFn: async () => {
      const result = await listBills(filter);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useBillSummary() {
  return useQuery({
    queryKey: billKeys.summary(),
    queryFn: async () => {
      const result = await getBillSummary();
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useQuickReminders() {
  return useQuery({
    queryKey: billKeys.reminders(),
    queryFn: async () => {
      const result = await getQuickReminders();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBillInput) => {
      const result = await createBill(input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useUpdateBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateBillInput }) => {
      const result = await updateBill(id, input);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteBill(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkBillPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await markPaid(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: async () => {
      await listBills();
      const result = await listUnread();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.count(),
    queryFn: async () => {
      await listBills();
      const result = await getUnreadCount();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? 0;
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await markRead(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await dismiss(id);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

// Re-export service for direct access if needed
export { budgetService, savingsGoalService, billService, notificationService };
